import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './History.module.css';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import ReplayViewer from './ReplayViewer';
import {
  GameRecordDocument,
  PlayerInfo,
  getGameRecordsForUser,
  getAllPlayers,
  updateGameName,
} from '../../services/firebase/gameRecordService';
import { hasDevAccess } from '../../utils/devPasscode';
import { playUISound } from '../../utils/UISoundEffects';

type ResultFilter = 'all' | 'win' | 'loss' | 'draw';

function History() {
  const navigate = useNavigate();
  const { user, accountType, profileLoading, userProfile } = useContext(AuthContext);

  const [gameRecords, setGameRecords] = useState<GameRecordDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState<GameRecordDocument | null>(null);
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // Dscvr-only toggle: switch between test and live game collections
  const isDscvr = userProfile?.username === 'Dscvr';
  const [useTestCollection, setUseTestCollection] = useState(isDscvr);
  const activeCollection = useTestCollection ? 'games-test' : 'games';

  // Dev-only: player switching (Dscvr account or dev passcode)
  const isDev = hasDevAccess() || userProfile?.username === 'Dscvr';
  const [allPlayers, setAllPlayers] = useState<PlayerInfo[]>([]);
  const [viewingPlayerId, setViewingPlayerId] = useState<string | null>(null);
  const [viewingPlayerName, setViewingPlayerName] = useState<string>('');

  const activePlayerId = viewingPlayerId || user?.uid || '';

  useEffect(() => {
    if (!profileLoading && accountType !== 'real') {
      navigate('/main');
    }
  }, [accountType, profileLoading, navigate]);

  // Dev-only: fetch all players for the switcher
  useEffect(() => {
    if (!isDev) return;
    getAllPlayers().then(setAllPlayers).catch(() => {});
  }, [isDev]);

  // Fetch game records for the active player from the selected collection
  useEffect(() => {
    if (!activePlayerId) return;
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const records = await getGameRecordsForUser(activePlayerId, 50, activeCollection);
        setGameRecords(records);
      } catch (err) {
      }
      setLoading(false);
    };
    fetchRecords();
  }, [activePlayerId, activeCollection]);

  if (profileLoading) {
    return (
      <div className={styles.container}>
        <GlobalHeader />
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (accountType !== 'real') {
    return null;
  }

  // If viewing a replay, show the ReplayViewer
  if (selectedRecord) {
    return (
      <ReplayViewer
        gameRecord={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    );
  }

  // Filter records
  const filteredRecords = gameRecords.filter((record) => {
    if (resultFilter === 'all') return true;
    const isLight = record.lightPlayerId === activePlayerId;
    if (resultFilter === 'win') {
      return record.result === 'draw'
        ? false
        : (record.result === 'light') === isLight;
    }
    if (resultFilter === 'loss') {
      return record.result === 'draw'
        ? false
        : (record.result === 'light') !== isLight;
    }
    if (resultFilter === 'draw') return record.result === 'draw';
    return true;
  });

  function getResultForUser(record: GameRecordDocument): 'win' | 'loss' | 'draw' {
    if (record.result === 'draw') return 'draw';
    const isLight = record.lightPlayerId === activePlayerId;
    return (record.result === 'light') === isLight ? 'win' : 'loss';
  }

  function getOpponentName(record: GameRecordDocument): string {
    const isLight = record.lightPlayerId === activePlayerId;
    return isLight ? record.darkPlayerUsername : record.lightPlayerUsername;
  }

  function formatDate(timestamp: number): string {
    const t = String(timestamp);
    const date = t.length === 8
      ? new Date(`${t.slice(0,4)}-${t.slice(4,6)}-${t.slice(6,8)}`)
      : new Date(timestamp);
    const day = date.getDate();
    const suffix = [11,12,13].includes(day) ? 'th' : ['st','nd','rd'][(day % 10) - 1] || 'th';
    const month = date.toLocaleString(undefined, { month: 'long' });
    return `${day}${suffix} of ${month} ${date.getFullYear()}`;
  }

  function handleStartEditing(record: GameRecordDocument, e: React.MouseEvent) {
    e.stopPropagation();
    setEditingId(record.id);
    setEditingValue(record.gameName || '');
  }

  async function handleSaveName(recordId: string) {
    const trimmed = editingValue.trim();
    setEditingId(null);
    try {
      await updateGameName(recordId, trimmed, activeCollection);
      setGameRecords((prev) =>
        prev.map((r) => (r.id === recordId ? { ...r, gameName: trimmed || undefined } : r))
      );
    } catch (err) {
    }
  }

  function handleNameKeyDown(e: React.KeyboardEvent, recordId: string) {
    if (e.key === 'Enter') handleSaveName(recordId);
    if (e.key === 'Escape') setEditingId(null);
  }

  const resultLabel = { win: 'Win', loss: 'Loss', draw: 'Draw' };
  const resultClass = { win: styles.resultWin, loss: styles.resultLoss, draw: styles.resultDraw };

  return (
    <div className={styles.container}>
      <GlobalHeader />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backArrow} onClick={() => { playUISound(); navigate('/main'); }}>
              &larr;
            </button>
            <h1 className={styles.title}>Game History</h1>
            {isDscvr && (
              <div className={styles.tabNavigation}>
                <button
                  className={`${styles.tab} ${useTestCollection ? styles.activeTab : ''}`}
                  onClick={() => setUseTestCollection(true)}
                >
                  Test Games
                </button>
                <button
                  className={`${styles.tab} ${!useTestCollection ? styles.activeTab : ''}`}
                  onClick={() => setUseTestCollection(false)}
                >
                  Live Games
                </button>
              </div>
            )}
            <div className={styles.tabNavigation}>
              {([
                { key: 'all' as ResultFilter, label: 'All' },
                { key: 'win' as ResultFilter, label: 'Wins' },
                { key: 'loss' as ResultFilter, label: 'Losses' },
                { key: 'draw' as ResultFilter, label: 'Draws' },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  className={`${styles.tab} ${resultFilter === key ? styles.activeTab : ''}`}
                  onClick={() => setResultFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.headerRight}>
            {isDev && allPlayers.length > 0 && (
              <select
                className={styles.devPlayerSelect}
                value={viewingPlayerId || ''}
                onChange={(e) => {
                  const uid = e.target.value;
                  if (!uid) {
                    setViewingPlayerId(null);
                    setViewingPlayerName('');
                  } else {
                    setViewingPlayerId(uid);
                    const player = allPlayers.find((p) => p.uid === uid);
                    setViewingPlayerName(player?.username || '');
                  }
                  setResultFilter('all');
                }}
              >
                <option value="">My Games</option>
                {allPlayers.map((p) => (
                  <option key={p.uid} value={p.uid}>
                    {p.username}
                  </option>
                ))}
              </select>
            )}
            {viewingPlayerName && (
              <span className={styles.devViewingLabel}>Viewing: {viewingPlayerName}</span>
            )}
            {!loading && <span className={styles.gameCount}>{filteredRecords.length} games</span>}
          </div>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading games...</div>
        ) : filteredRecords.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>&#9813;</div>
            <h2 className={styles.emptyTitle}>
              {gameRecords.length === 0
                ? 'No games recorded yet'
                : 'No matching games'}
            </h2>
            <p className={styles.emptyText}>
              {gameRecords.length === 0
                ? 'Play multiplayer games to build your history.'
                : 'Try a different filter.'}
            </p>
          </div>
        ) : (
          <div className={styles.listContainer}>
            {filteredRecords.map((record) => {
              const result = getResultForUser(record);
              const opponent = getOpponentName(record);
              const displayName = record.gameName || `Game vs ${opponent}`;

              return (
                <div
                  key={record.id}
                  className={styles.gameRow}
                  onClick={() => setSelectedRecord(record)}
                >
                  <div className={`${styles.resultBadge} ${resultClass[result]}`}>
                    {resultLabel[result]}
                  </div>
                  <div className={styles.gameInfo}>
                    <div className={styles.gameName}>
                      {displayName}
                    </div>
                    <div className={styles.gameDetails}>
                      vs {opponent} &middot; {record.turnCount} turns &middot; {record.timeControlPreset} &middot; {formatDate(record.timestamp)}
                    </div>
                  </div>
                  {editingId === record.id ? (
                    <input
                      className={styles.nameInput}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={() => handleSaveName(record.id)}
                      onKeyDown={(e) => handleNameKeyDown(e, record.id)}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Enter game name..."
                      maxLength={60}
                    />
                  ) : (
                    <button
                      className={styles.renameButton}
                      onClick={(e) => handleStartEditing(record, e)}
                      title="Rename this game"
                    >
                      Rename
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default History;
